import { styled } from '@linaria/react';
import { colors, component, fonts, layout, semantic, space, textStyle } from '../../theme/tokens';

export const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${space[2]};
`;

export const StyledButton = styled.button<{
  $fullWidth: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${textStyle.body.lg.strong.fontFamily};
  font-weight: ${textStyle.body.lg.strong.fontWeight};
  letter-spacing: ${textStyle.body.lg.strong.letterSpacing};
  line-height: 1;
  border-radius: ${layout.borderRadiusFull};
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  padding: ${space[3]} ${space['20px']};
  font-size: ${textStyle.body.lg.strong.fontSize};

  &[data-size='small'] {
    padding: ${space[2]} ${space[4]};
    font-size: ${textStyle.body.sm.strong.fontSize};
  }

  &[data-size='large'] {
    padding: ${space[4]} ${space['20px']};
    font-size: ${textStyle.body.lg.strong.fontSize};
  }

  background-color: ${component.button.primary.enabled.bg};
  color: ${component.button.primary.enabled.text};
  border: 2px solid ${component.button.primary.enabled.bg};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }

  &[data-variant='primary'] {
    &:disabled {
      background-color: ${component.button.primary.disabled.bg};
      color: ${component.button.primary.disabled.text};
      border-color: ${component.button.primary.disabled.bg};
      opacity: 1;
    }

    &:focus-visible {
      background-color: ${component.button.primary.focussed.bg};
      color: ${component.button.primary.focussed.text};
    }

    &:hover:not(:disabled) {
      background-color: ${component.button.primary.hover.bg};
      border-color: ${component.button.primary.hover.bg};
      color: ${component.button.primary.hover.text};
    }

    &:active:not(:disabled) {
      background-color: ${component.button.primary.pressed.bg};
      border-color: ${component.button.primary.pressed.bg};
      color: ${component.button.primary.pressed.text};
    }
  }

  &:not([data-variant='primary']):hover:not(:disabled) {
    opacity: 0.85;
  }

  &[data-variant='secondary'] {
    background-color: ${semantic.bg.surfaceSubtle};
    color: ${semantic.text.primary};
    border: 2px solid ${semantic.border.subtle};

    &:hover:not(:disabled) {
      background-color: ${semantic.bg.surfaceStrong};
      border-color: ${semantic.border.default};
      opacity: 1;
    }
  }

  &[data-variant='outline'] {
    background-color: transparent;
    color: ${semantic.text.primary};
    border: 2px solid ${semantic.border.selected};

    &:hover:not(:disabled) {
      background-color: ${semantic.border.selected};
      color: ${colors.white};
      opacity: 1;
    }
  }

  &[data-variant='toggle'] {
    background-color: transparent;
    color: ${semantic.text.primary};
    border: 1px solid ${semantic.border.default};
    border-radius: 8px;
    font-weight: ${fonts.weight.medium};

    &:hover:not(:disabled) {
      background-color: ${semantic.bg.surfaceSubtle};
      border-color: ${semantic.border.default};
      color: ${semantic.text.primary};
    }

    &[data-selected] {
      background-color: ${colors.primary};
      border-color: ${colors.primary};
      color: ${colors.white};

      &:hover:not(:disabled) {
        background-color: ${colors.primary};
        border-color: ${colors.primary};
        color: ${colors.white};
        opacity: 0.85;
      }
    }
  }

  &[data-variant='icon'] {
    height: 40px;
    padding: ${space[2]} ${space[4]};
    border-radius: ${layout.borderRadiusFull};
    background: ${semantic.bg.surfaceSubtle};
    color: ${semantic.text.primary};
    border: none;

    &:hover:not(:disabled) {
      background: ${semantic.bg.surfaceStrong};
      opacity: 1;
    }
  }

  &[data-variant='danger'] {
    background-color: ${semantic.bg.dangerStrong};
    color: ${semantic.text.inverse};
    border: 2px solid ${semantic.bg.dangerStrong};

    &:focus-visible {
      outline-color: ${semantic.bg.dangerStrong};
    }
  }

  &[data-variant='icon-ghost'] {
    height: 40px;
    padding: ${space[1]} ${space[4]};
    border-radius: ${layout.borderRadiusFull};
    background: transparent;
    color: ${semantic.text.primary};
    border: none;

    &:focus-visible {
      outline-offset: -2px;
    }

    &:hover:not(:disabled) {
      background: ${semantic.bg.surfaceStrong};
      opacity: 1;
    }
  }
`;
